import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, MapPin, Rainbow, Ticket } from "lucide-react";
import api from "@/api";
import ChapterHeading from "@/components/home/ChapterHeading";
import MemberModal from "@/components/home/MemberModal";

const COMMUNITY_CATS = ["LGBTQ+", "Lifestyle (LS)"];

const catStyle = {
  "LGBTQ+": "border-neon-magenta/50 bg-neon-magenta/10 text-neon-magenta",
  "Lifestyle (LS)": "border-neon-purple/50 bg-neon-purple/10 text-neon-purple",
};

function formatDate(iso) {
  return new Date(`${iso}T12:00:00`)
    .toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
    .toUpperCase();
}

export default function CommunitySection({ cityId }) {
  const [events, setEvents] = useState([]);
  const [memberOpen, setMemberOpen] = useState(false);
  const [isMember, setIsMember] = useState(() => !!localStorage.getItem("ad-member-token"));

  const fetchEvents = () => {
    api
      .get(`/events?city=${cityId}`)
      .then(({ data }) => setEvents(data.filter((e) => COMMUNITY_CATS.includes(e.category))))
      .catch(() => {});
  };

  useEffect(() => {
    fetchEvents();
  }, [cityId]);

  if (events.length === 0) return null;

  return (
    <section className="border-b border-white/10 bg-[#0E1117] py-24" data-testid="community-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <ChapterHeading
          num="02 // PRIDE & LIFESTYLE"
          title="Every Kind of Night"
          accent="text-neon-magenta"
          sub="Gayborhood drag revues, queer vinyl nights, and velvet-rope lifestyle socials — inclusive floors, zero judgment, strict door codes where it counts."
        />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((e, i) => (
            <motion.article
              key={e.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="group overflow-hidden rounded-2xl border border-white/10 bg-[#131722] transition-all hover:-translate-y-1.5 hover:border-neon-magenta/40 hover:shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
              data-testid={`community-card-${e.id}`}
            >
              <div className="relative h-52 overflow-hidden">
                <img src={e.image} alt={e.name} className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-110 ${e.locked ? "opacity-40 blur-[2px]" : ""}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#131722] via-transparent to-transparent" />
                <span className={`absolute left-4 top-4 rounded-full border px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] backdrop-blur-md ${catStyle[e.category] || "border-white/30 bg-black/50 text-white"}`}>
                  {e.category}
                </span>
                <span className="absolute right-4 top-4 rounded-full border border-white/20 bg-black/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white backdrop-blur-md">
                  {e.price}
                </span>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-neon-magenta">
                  <span className="flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {formatDate(e.date)} — {e.time}
                  </span>
                </div>
                <h3 className="mt-3 font-display text-xl font-bold uppercase leading-tight tracking-tight">{e.name}</h3>
                <p className="mt-2 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                  <MapPin className="h-3.5 w-3.5" />
                  {e.venue} — {e.neighborhood}
                </p>
                <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-zinc-400">{e.description}</p>
                {e.locked && !isMember ? (
                  <button
                    onClick={() => setMemberOpen(true)}
                    data-testid={`community-unlock-button-${e.id}`}
                    className="mt-5 inline-flex items-center gap-2 rounded-full border border-neon-purple/50 px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-neon-purple transition-all hover:-translate-y-0.5 hover:bg-neon-purple hover:text-black"
                  >
                    <Ticket className="h-4 w-4" /> Unlock with Email
                  </button>
                ) : e.external_link ? (
                  <a
                    href={e.external_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`community-tickets-link-${e.id}`}
                    className="mt-5 inline-flex items-center gap-2 rounded-full border border-neon-cyan/50 px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-neon-cyan transition-all hover:-translate-y-0.5 hover:bg-neon-cyan hover:text-black"
                  >
                    <Ticket className="h-4 w-4" /> Get Tickets ↗
                  </a>
                ) : null}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
      <MemberModal open={memberOpen} onClose={() => setMemberOpen(false)} onVerified={() => { setIsMember(true); fetchEvents(); }} />
    </section>
  );
}
